import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, MapPin, User, ArrowLeft, Star, Gift, Award, ChevronRight, Shield, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../config';

const ClientPortal = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsLookingUp(true);
    setError('');
    
    try {
      const [bookingsRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/client/bookings`, { params: { email: email.trim() } }),
        axios.get(`${API}/client/loyalty`, { params: { email: email.trim() } })
      ]);
      setBookings(bookingsRes.data);
      setLoyalty(loyaltyRes.data);
    } catch (err) {
      setError('Could not find your bookings. Please check your email address.');
      setBookings(null);
      setLoyalty(null);
    }
    setIsLookingUp(false);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'Gold': return 'from-amber-400 to-yellow-500 text-amber-900';
      case 'Silver': return 'from-slate-300 to-slate-400 text-slate-800';
      case 'Bronze': return 'from-orange-300 to-amber-400 text-orange-900';
      default: return 'from-sage-400 to-sage-500 text-sage-900';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const upcoming = bookings?.filter(b => b.status === 'pending' || b.status === 'confirmed') || [];
  const past = bookings?.filter(b => b.status === 'completed' || b.status === 'cancelled') || [];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="client-portal">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button variant="ghost" onClick={onBack} className="p-2" data-testid="client-portal-back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-stone-800">My Bookings</h1>
              <p className="text-sm text-stone-500">View and manage your appointments</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Email Lookup */}
        {!bookings && (
          <div className="max-w-md mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-sage-100 rounded-full mb-6">
              <Search className="text-sage-600" size={28} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-800 mb-2">Find Your Bookings</h2>
            <p className="text-stone-500 mb-8">Enter the email address you used when booking</p>
            
            <form onSubmit={handleLookup} className="space-y-4">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="text-center text-lg py-6"
                required
                data-testid="client-email-input"
              />
              <Button 
                type="submit" 
                className="w-full bg-sage-600 hover:bg-sage-700 text-white py-6 rounded-full text-lg"
                disabled={isLookingUp}
                data-testid="client-lookup-btn"
              >
                {isLookingUp ? 'Looking up...' : 'View My Bookings'}
              </Button>
            </form>
            
            {error && (
              <p className="text-red-500 mt-4 text-sm" data-testid="client-error">{error}</p>
            )}
          </div>
        )}

        {/* Results */}
        {bookings && (
          <div className="space-y-6">
            {/* Change Email */}
            <div className="flex items-center justify-between">
              <p className="text-stone-500 text-sm">Showing bookings for <strong>{email}</strong></p>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setBookings(null); setLoyalty(null); setEmail(''); }}
                className="text-sage-600"
                data-testid="client-change-email"
              >
                Change email
              </Button>
            </div>

            {/* Loyalty Card */}
            {loyalty && (
              <div className={`bg-gradient-to-br ${getTierColor(loyalty.tier)} rounded-2xl p-6 shadow-lg relative overflow-hidden`} data-testid="client-loyalty-card">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <Award size={20} />
                    <span className="font-semibold text-sm uppercase tracking-wider">{loyalty.tier} Member</span>
                  </div>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-4xl font-bold">{loyalty.total_visits}</span>
                    <span className="text-sm opacity-80">visits</span>
                  </div>
                  {loyalty.current_discount_percent > 0 && (
                    <p className="text-sm font-medium opacity-90">
                      You enjoy {loyalty.current_discount_percent}% loyalty discount
                    </p>
                  )}
                  {loyalty.next_discount && (
                    <p className="text-xs mt-2 opacity-70">
                      {loyalty.next_discount.visits_needed} more visit{loyalty.next_discount.visits_needed > 1 ? 's' : ''} to unlock {loyalty.next_discount.discount} discount
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Bookings */}
            <div>
              <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-sage-600" />
                Upcoming Appointments ({upcoming.length})
              </h3>
              {upcoming.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center border border-stone-200">
                  <Calendar size={32} className="mx-auto text-stone-300 mb-3" />
                  <p className="text-stone-500">No upcoming appointments</p>
                  <Button 
                    onClick={onBack}
                    className="mt-4 bg-sage-600 hover:bg-sage-700 text-white rounded-full px-6"
                  >
                    Book Now
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((booking) => (
                    <div key={booking.id} className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm" data-testid="upcoming-booking-card">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-semibold text-stone-800">{booking.service_name}</p>
                          <div className="flex items-center gap-3 text-sm text-stone-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(booking.booking_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {booking.booking_time}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-stone-500">
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span className="capitalize">{booking.location?.replace('_', ' ')}</span>
                            </span>
                            {booking.therapist_preference && booking.therapist_preference !== 'no_preference' && (
                              <span className="flex items-center gap-1">
                                <User size={14} />
                                <span className="capitalize">{booking.therapist_preference}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                          <p className="text-lg font-bold text-sage-600 mt-1">€{booking.service_price}</p>
                          {booking.online_discount_applied > 0 && (
                            <p className="text-xs text-emerald-600">5% online discount</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Past Bookings */}
            {past.length > 0 && (
              <div>
                <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                  <Clock size={18} className="text-stone-400" />
                  Past Appointments ({past.length})
                </h3>
                <div className="space-y-2">
                  {past.map((booking) => (
                    <div key={booking.id} className="bg-white/60 rounded-xl p-4 border border-stone-100" data-testid="past-booking-card">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-stone-700">{booking.service_name}</p>
                          <p className="text-sm text-stone-400">
                            {formatDate(booking.booking_date)} at {booking.booking_time}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.status === 'completed' && (
                            <button
                              onClick={() => {
                                const params = new URLSearchParams({
                                  service: booking.service_name,
                                  location: booking.location,
                                  therapist: booking.therapist_preference || 'no_preference'
                                });
                                window.location.href = `/?rebook=${params.toString()}`;
                              }}
                              className="flex items-center gap-1.5 text-sage-600 hover:text-sage-700 bg-sage-50 hover:bg-sage-100 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                              data-testid="rebook-btn"
                            >
                              <RotateCcw size={12} />
                              Rebook
                            </button>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientPortal;
